import type { ReactElement } from 'react';
import { useEffect, useRef, useState } from 'react';
import type { ChatAttachment, CreditsResponse } from '@shared/api';
import { useShellState } from '../components/AppShell';
import { AgentBar } from '../components/dock/ComposerDock';
import { createProposal, getCredits } from '../api/client';
import { creditsLine } from '../lib/credits';
import type { BuildEngine } from '../api/client';
import { navigate, proposalPath } from '../state/router';
import { stashFirstMessage } from '../state/pendingMessage';
import { useFeatureFlags } from '../state/FeatureFlagsContext';

/**
 * EMPTY canvas: one composer, nothing disabled — the only thing to do here is
 * start talking. Module chrome (brand, nav, recent proposals, account) lives
 * in the persistent sidebar (design.md V9), not on this page.
 *
 * Build Engine Selector v1 is shelved behind PROPOSAL_EXPERIMENTAL_ENGINES
 * (src/build-engines/README.md): an engine picker sits under the prompt —
 * Classic (default), Studio (beta), and Forge (experimental) — but ONLY when
 * `features.experimentalEngines` is true; with the master flag off (the
 * default) there is no picker at all, exactly the single-engine flow from
 * before Studio existed.
 */
/**
 * Whether the engine picker should render at all — hooks-free so it's
 * unit-testable without a React renderer (test/web/start-view.test.ts).
 * With the master flag off, the answer is always false: the single-engine
 * flow from before Studio existed, no matter what proposalStudio/
 * proposalForge individually say.
 */
export function shouldShowEnginePicker(
  experimentalEngines: boolean,
  studioAvailable: boolean,
  forgeAvailable: boolean,
): boolean {
  return experimentalEngines && (studioAvailable || forgeAvailable);
}

export function StartView(): ReactElement {
  useShellState({});
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const launchStarted = useRef(false);
  const { experimentalEngines, proposalStudio: studioAvailable, proposalForge: forgeAvailable } =
    useFeatureFlags();
  const [buildEngine, setBuildEngine] = useState<BuildEngine>('classic');
  const [credits, setCredits] = useState<CreditsResponse | null>(null);

  // docs/limits.md: supplemental — a failed fetch just hides the line; the
  // server still refuses a create the rep has no credit for.
  useEffect(() => {
    let live = true;
    getCredits()
      .then((next) => {
        if (live) setCredits(next);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);
  const creditNote = creditsLine(credits);

  const start = async (message: string, attachments: ChatAttachment[] = []) => {
    if (launchStarted.current) return;
    launchStarted.current = true;
    setStarting(true);
    setError(null);
    window.dispatchEvent(new CustomEvent('viq:analytics', {
      detail: { event: 'proposal_intake_started' },
    }));
    return createProposal(message, buildEngine)
      .then((res) => {
        stashFirstMessage(res.proposal.proposalId, message, attachments);
        navigate(proposalPath(res.proposal.proposalId));
      })
      .catch((cause: unknown) => {
        launchStarted.current = false;
        setStarting(false);
        setError(cause instanceof Error ? cause.message : String(cause));
        throw cause;
      });
  };

  return (
    <main className="start" aria-busy={starting}>
      <div className="start-inner">
        <div className="start-prompt">
          <h1 className="start-title">Who are you pitching?</h1>
          <p className="start-help">
            A name, website, or short description is enough. I’ll research them and build the plan.
          </p>
        </div>
        <div className="start-composer-stage">
          <AgentBar
            onSend={start}
            disabled={starting || creditNote?.blocked === true}
            placeholder="Brody Pennell, $40k summer campaign"
            mode="start"
            autoFocus
            clearOnSend={false}
          />
        </div>
        {error ? (
          <p className="start-error">{error}</p>
        ) : creditNote ? (
          <p className={creditNote.blocked ? 'start-error' : 'start-credits'}>{creditNote.text}</p>
        ) : null}
        {shouldShowEnginePicker(experimentalEngines, studioAvailable, forgeAvailable) ? (
          <div
            className={`start-engine-picker${studioAvailable && forgeAvailable ? ' start-engine-picker--3' : ''}`}
            role="radiogroup"
            aria-label="Build engine"
          >
            <button
              type="button"
              role="radio"
              aria-checked={buildEngine === 'classic'}
              disabled={starting}
              className={`start-engine-card${buildEngine === 'classic' ? ' start-engine-card--selected' : ''}`}
              onClick={() => setBuildEngine('classic')}
            >
              <span className="start-engine-card-title">Classic</span>
              <span className="start-engine-card-desc">Proven layouts · Fast and consistent</span>
            </button>
            {studioAvailable ? (
              <button
                type="button"
                role="radio"
                aria-checked={buildEngine === 'studio'}
                disabled={starting}
                className={`start-engine-card${buildEngine === 'studio' ? ' start-engine-card--selected' : ''}`}
                onClick={() => setBuildEngine('studio')}
              >
                <span className="start-engine-card-title">
                  Studio <span className="start-engine-card-badge">Beta</span>
                </span>
                <span className="start-engine-card-desc">Dynamic compositions · Built from design-system primitives</span>
              </button>
            ) : null}
            {forgeAvailable ? (
              <button
                type="button"
                role="radio"
                aria-checked={buildEngine === 'forge'}
                disabled={starting}
                className={`start-engine-card${buildEngine === 'forge' ? ' start-engine-card--selected' : ''}`}
                onClick={() => setBuildEngine('forge')}
              >
                <span className="start-engine-card-title">
                  Forge <span className="start-engine-card-badge">Experimental</span>
                </span>
                <span className="start-engine-card-desc">Agent-built from scratch · Maximum visual freedom</span>
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </main>
  );
}
